import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ROOT = window.Shopify?.routes?.root || '/';
const OPT_OUT_ATTRIBUTE = '_shipping_protection_opt_out';

/**
 * <shipping-protection-toggle> — adds or removes the Shipping Protection product.
 * On by default: it is added automatically whenever the cart has other items,
 * unless the customer switched it off (remembered on the cart as a hidden attribute).
 * Dispatches the standard cart event so the theme's cart drawer / cart page
 * and the cart icon re-render exactly as they do for a normal quantity change.
 */
class ShippingProtectionToggle extends HTMLElement {
  static observedAttributes = ['data-in-cart', 'data-other-count', 'data-opted-out'];

  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#reconcile();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue !== newValue && this.isConnected) this.#reconcile();
  }

  get variantId() {
    return Number(this.dataset.variantId);
  }

  get inCart() {
    return this.dataset.inCart === 'true';
  }

  get otherCount() {
    return Number(this.dataset.otherCount || 0);
  }

  get optedOut() {
    return this.dataset.optedOut === 'true';
  }

  get switchElement() {
    return this.querySelector('[role="switch"]');
  }

  #reconcile() {
    if (this.#busy) return;

    // Never leave protection alone in a cart with nothing else in it.
    if (this.inCart && this.otherCount === 0) {
      this.#setProtection(false, { userAction: false });
      return;
    }

    // On by default: add it unless the customer turned it off.
    if (!this.inCart && this.otherCount > 0 && !this.optedOut) {
      this.#setProtection(true, { userAction: false });
    }
  }

  #onClick = (event) => {
    event.preventDefault();
    if (this.#busy) return;

    const next = this.switchElement?.getAttribute('aria-checked') !== 'true';
    this.switchElement?.setAttribute('aria-checked', String(next));
    this.#setProtection(next, { userAction: true });
  };

  #sectionIds() {
    const ids = new Set();
    for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    }
    return Array.from(ids).join(',');
  }

  async #post(path, payload, withSections = true) {
    const body = withSections
      ? { ...payload, sections: this.#sectionIds(), sections_url: window.location.pathname }
      : payload;
    const response = await fetch(`${ROOT}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Cart update failed');
    }
    return data;
  }

  async #getCart() {
    const response = await fetch(`${ROOT}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** Remembers the customer's choice on the cart so it isn't re-added automatically. */
  async #rememberChoice(enabled) {
    await this.#post('cart/update.js', { attributes: { [OPT_OUT_ATTRIBUTE]: enabled ? '' : '1' } }, false);
  }

  /**
   * @param {boolean} enable
   * @param {{ userAction: boolean }} options
   */
  async #setProtection(enable, { userAction }) {
    if (this.#busy) return;
    this.#busy = true;
    this.classList.add('is-loading');

    const deferred = CartLinesUpdateEvent.createPromise();
    let dispatched = false;

    try {
      if (userAction) await this.#rememberChoice(enable);

      let data;
      let cart;

      if (enable) {
        const current = await this.#getCart();
        if (current.items.some((item) => item.variant_id === this.variantId)) return;

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'cart',
            lines: [{ merchandiseId: String(this.variantId), quantity: 1 }],
            promise: deferred.promise,
          })
        );
        dispatched = true;

        data = await this.#post('cart/add.js', { items: [{ id: this.variantId, quantity: 1 }] });
        cart = await this.#getCart();
      } else {
        const current = await this.#getCart();
        const line = current.items.find((item) => item.variant_id === this.variantId);
        if (!line) return;

        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'remove',
            context: 'cart',
            lines: [{ id: line.key, quantity: 0 }],
            promise: deferred.promise,
          })
        );
        dispatched = true;

        data = await this.#post('cart/change.js', { id: line.key, quantity: 0 });
        cart = data;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: data.sections ?? undefined,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'shipping-protection',
          didError: false,
        },
      });
    } catch (error) {
      console.error('[shipping-protection]', error);
      if (dispatched) deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Cart update failed', code: 'SERVICE_UNAVAILABLE' }));
      this.switchElement?.setAttribute('aria-checked', String(this.inCart));
    } finally {
      this.#busy = false;
      this.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('shipping-protection-toggle')) {
  customElements.define('shipping-protection-toggle', ShippingProtectionToggle);
}
